import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import ApiBaseUrl from "../constants/apiUrl";
import {
  Briefcase,
  ArrowLeft,
  Save,
  Plus,
  Trash2,
  CheckCircle,
  AlertCircle,
  Building,
  MapPin,
  Clock,
  DollarSign,
  Award,
} from "lucide-react";

const CareerJobAdd = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  const [formData, setFormData] = useState({
    title: "",
    department: "Engineering & IT",
    jobType: "Full-time",
    workplaceType: "On-site",
    location: "Gulberg III, Lahore, Pakistan",
    experienceLevel: "1-2 Years",
    salaryRange: "PKR 60,000 - 90,000 / month",
    salaryMin: 60000,
    salaryMax: 90000,
    openings: 1,
    description: "",
    requirements: [
      "Bachelor's degree in Computer Science, Business, or relevant discipline.",
      "Proven working experience in a similar role or startup environment.",
      "Strong communication, interpersonal, and analytical skills.",
    ],
    responsibilities: [
      "Collaborate with team leads and cross-functional members to achieve targets.",
      "Manage day-to-day operations and deliver high-standard outputs on time.",
      "Identify areas for process improvement and innovative service delivery.",
    ],
    benefits: [
      "Market competitive salary package with performance bonuses.",
      "Comprehensive medical insurance for employee & family.",
      "Paid annual leaves, festival bonuses, and daily office lunch/refreshments.",
      "Fast-track growth opportunities in a scaling platform.",
    ],
    skills: ["Communication", "Problem Solving", "Teamwork"],
    deadline: "",
    status: "active",
    featured: false,
  });

  const [currentSkill, setCurrentSkill] = useState("");

  const departmentsList = [
    "Engineering & IT",
    "Sales & Marketing",
    "Operations & Logistics",
    "Finance & Accounts",
    "Customer Support",
    "Human Resources",
    "Legal & Compliance",
    "Design & Creative",
    "Executive Management",
  ];

  const handleArrayChange = (field, index, value) => {
    const updated = [...formData[field]];
    updated[index] = value;
    setFormData({ ...formData, [field]: updated });
  };

  const handleAddArrayItem = (field) => {
    setFormData({ ...formData, [field]: [...formData[field], ""] });
  };

  const handleRemoveArrayItem = (field, index) => {
    const updated = formData[field].filter((_, i) => i !== index);
    setFormData({ ...formData, [field]: updated });
  };

  const handleAddSkill = (e) => {
    e.preventDefault();
    if (currentSkill.trim() && !formData.skills.includes(currentSkill.trim())) {
      setFormData({
        ...formData,
        skills: [...formData.skills, currentSkill.trim()],
      });
      setCurrentSkill("");
    }
  };

  const handleRemoveSkill = (skillToRemove) => {
    setFormData({
      ...formData,
      skills: formData.skills.filter((s) => s !== skillToRemove),
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.title.trim() || !formData.description.trim()) {
      setError("Please fill in the Job Title and Job Description.");
      return;
    }

    setLoading(true);
    setError(null);
    setSuccess(null);

    try {
      const authData = JSON.parse(localStorage.getItem("adminAuth"));
      const payload = {
        ...formData,
        requirements: formData.requirements.filter((r) => r.trim().length > 0),
        responsibilities: formData.responsibilities.filter((r) => r.trim().length > 0),
        benefits: formData.benefits.filter((b) => b.trim().length > 0),
      };

      const res = await fetch(`${ApiBaseUrl}/admin/career/jobs`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${authData?.token || ""}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify(payload),
      });

      const data = await res.json();
      if (data.success) {
        setSuccess("Job posting published successfully!");
        setTimeout(() => {
          navigate("/career/jobs");
        }, 1200);
      } else {
        setError(data.message || "Failed to create job posting");
      }
    } catch (err) {
      console.error(err);
      setError("Server connection failed. Could not save job.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="p-4 sm:p-6 lg:p-8 max-w-5xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div className="flex items-center gap-4">
          <Link
            to="/career/jobs"
            className="p-2.5 rounded-xl border border-gray-200 bg-white hover:bg-gray-50 text-gray-600 transition shadow-sm"
          >
            <ArrowLeft className="w-5 h-5" />
          </Link>
          <div>
            <div className="flex items-center gap-2 text-sm text-gray-500 mb-1">
              <Link to="/career/jobs" className="hover:text-red-600">Job Postings</Link>
              <span>/</span>
              <span className="text-gray-900 font-medium">Add Position</span>
            </div>
            <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">
              Create New Job Vacancy
            </h1>
          </div>
        </div>

        <button
          type="button"
          onClick={handleSubmit}
          disabled={loading}
          className="inline-flex items-center gap-2 px-6 py-2.5 bg-red-600 hover:bg-red-700 text-white rounded-xl text-sm font-semibold shadow-md transition disabled:opacity-50"
        >
          {loading ? (
            <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
          ) : (
            <Save className="w-4 h-4" />
          )}
          Publish Job
        </button>
      </div>

      {error && (
        <div className="mb-6 p-4 rounded-xl bg-red-50 border border-red-200 text-red-700 flex items-center gap-3">
          <AlertCircle className="w-5 h-5 shrink-0" />
          <p className="text-sm font-medium">{error}</p>
        </div>
      )}

      {success && (
        <div className="mb-6 p-4 rounded-xl bg-emerald-50 border border-emerald-200 text-emerald-700 flex items-center gap-3">
          <CheckCircle className="w-5 h-5 shrink-0" />
          <p className="text-sm font-medium">{success}</p>
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-8">
        {/* Basic Position Details */}
        <div className="bg-white p-6 rounded-2xl border border-gray-200 shadow-sm space-y-6">
          <h2 className="text-lg font-bold text-gray-900 pb-3 border-b border-gray-100 flex items-center gap-2">
            <Briefcase className="w-5 h-5 text-red-600" />
            General Information
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="md:col-span-2">
              <label className="block text-sm font-semibold text-gray-800 mb-2">
                Job Title <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                placeholder="e.g. Senior Full Stack Engineer, Property Sales Executive"
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                required
                className="w-full px-4 py-2.5 bg-gray-50 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-red-500 focus:bg-white"
              />
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-800 mb-2">
                Department <span className="text-red-500">*</span>
              </label>
              <select
                value={formData.department}
                onChange={(e) => setFormData({ ...formData, department: e.target.value })}
                className="w-full px-4 py-2.5 bg-gray-50 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-red-500"
              >
                {departmentsList.map((d) => (
                  <option key={d} value={d}>
                    {d}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-800 mb-2">
                Job Type
              </label>
              <select
                value={formData.jobType}
                onChange={(e) => setFormData({ ...formData, jobType: e.target.value })}
                className="w-full px-4 py-2.5 bg-gray-50 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-red-500"
              >
                <option value="Full-time">Full-time</option>
                <option value="Part-time">Part-time</option>
                <option value="Remote">Remote</option>
                <option value="Hybrid">Hybrid</option>
                <option value="Contract">Contract</option>
                <option value="Internship">Internship</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-800 mb-2">
                Workplace Environment
              </label>
              <select
                value={formData.workplaceType}
                onChange={(e) => setFormData({ ...formData, workplaceType: e.target.value })}
                className="w-full px-4 py-2.5 bg-gray-50 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-red-500"
              >
                <option value="On-site">On-site (Office)</option>
                <option value="Remote">Remote (Work from home)</option>
                <option value="Hybrid">Hybrid (Flexible)</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-800 mb-2">
                Location / City <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={formData.location}
                onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                placeholder="e.g. Lahore, Pakistan or Karachi, Pakistan"
                className="w-full px-4 py-2.5 bg-gray-50 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-red-500 focus:bg-white"
              />
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-800 mb-2">
                Experience Level
              </label>
              <select
                value={formData.experienceLevel}
                onChange={(e) => setFormData({ ...formData, experienceLevel: e.target.value })}
                className="w-full px-4 py-2.5 bg-gray-50 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-red-500"
              >
                <option value="Fresh / Entry Level">Fresh / Entry Level</option>
                <option value="1-2 Years">1-2 Years</option>
                <option value="2-4 Years">2-4 Years</option>
                <option value="4-6 Years">4-6 Years</option>
                <option value="6+ Years">6+ Years</option>
                <option value="Lead / Executive">Lead / Executive</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-800 mb-2">
                Salary Range / Package
              </label>
              <input
                type="text"
                value={formData.salaryRange}
                onChange={(e) => setFormData({ ...formData, salaryRange: e.target.value })}
                placeholder="e.g. PKR 75,000 - 100,000 / month or Market Competitive"
                className="w-full px-4 py-2.5 bg-gray-50 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-red-500 focus:bg-white"
              />
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-800 mb-2">
                Number of Openings
              </label>
              <input
                type="number"
                min="1"
                value={formData.openings}
                onChange={(e) => setFormData({ ...formData, openings: parseInt(e.target.value, 10) || 1 })}
                className="w-full px-4 py-2.5 bg-gray-50 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-red-500 focus:bg-white"
              />
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-800 mb-2">
                Application Deadline (Optional)
              </label>
              <input
                type="date"
                value={formData.deadline}
                onChange={(e) => setFormData({ ...formData, deadline: e.target.value })}
                className="w-full px-4 py-2.5 bg-gray-50 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-red-500 focus:bg-white"
              />
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-800 mb-2">
                Listing Status
              </label>
              <select
                value={formData.status}
                onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                className="w-full px-4 py-2.5 bg-gray-50 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-red-500"
              >
                <option value="active">Active (Visible to public)</option>
                <option value="inactive">Inactive (Hidden)</option>
                <option value="draft">Draft</option>
              </select>
            </div>

            <div className="flex items-center gap-3 pt-6">
              <label className="relative flex items-center gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={formData.featured}
                  onChange={(e) => setFormData({ ...formData, featured: e.target.checked })}
                  className="w-5 h-5 text-red-600 rounded border-gray-300 focus:ring-red-500"
                />
                <span className="text-sm font-semibold text-gray-800">
                  Feature this job on top of careers page
                </span>
              </label>
            </div>
          </div>
        </div>

        {/* Job Overview & Description */}
        <div className="bg-white p-6 rounded-2xl border border-gray-200 shadow-sm space-y-4">
          <h2 className="text-lg font-bold text-gray-900 pb-3 border-b border-gray-100">
            About the Role & Overview <span className="text-red-500">*</span>
          </h2>
          <div>
            <textarea
              rows="6"
              placeholder="Describe what this role entails, what the candidate will be doing, and why it matters at Madadgaar..."
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              required
              className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-red-500 focus:bg-white leading-relaxed"
            />
          </div>
        </div>

        {/* Responsibilities */}
        <div className="bg-white p-6 rounded-2xl border border-gray-200 shadow-sm space-y-4">
          <div className="flex items-center justify-between pb-3 border-b border-gray-100">
            <h2 className="text-lg font-bold text-gray-900">Key Responsibilities</h2>
            <button
              type="button"
              onClick={() => handleAddArrayItem("responsibilities")}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-gray-100 hover:bg-gray-200 text-gray-700 text-xs font-semibold rounded-lg transition"
            >
              <Plus className="w-3.5 h-3.5" /> Add Point
            </button>
          </div>

          <div className="space-y-3">
            {formData.responsibilities.map((resp, idx) => (
              <div key={idx} className="flex items-center gap-2">
                <span className="w-6 text-xs text-gray-400 font-bold text-center">#{idx + 1}</span>
                <input
                  type="text"
                  value={resp}
                  onChange={(e) => handleArrayChange("responsibilities", idx, e.target.value)}
                  placeholder="Enter a key responsibility..."
                  className="flex-1 px-4 py-2.5 bg-gray-50 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-red-500 focus:bg-white"
                />
                <button
                  type="button"
                  onClick={() => handleRemoveArrayItem("responsibilities", idx)}
                  className="p-2 text-gray-400 hover:text-red-600 rounded-lg hover:bg-red-50 transition"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            ))}
          </div>
        </div>

        {/* Requirements & Qualifications */}
        <div className="bg-white p-6 rounded-2xl border border-gray-200 shadow-sm space-y-4">
          <div className="flex items-center justify-between pb-3 border-b border-gray-100">
            <h2 className="text-lg font-bold text-gray-900">Requirements & Qualifications</h2>
            <button
              type="button"
              onClick={() => handleAddArrayItem("requirements")}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-gray-100 hover:bg-gray-200 text-gray-700 text-xs font-semibold rounded-lg transition"
            >
              <Plus className="w-3.5 h-3.5" /> Add Requirement
            </button>
          </div>

          <div className="space-y-3">
            {formData.requirements.map((req, idx) => (
              <div key={idx} className="flex items-center gap-2">
                <span className="w-6 text-xs text-gray-400 font-bold text-center">#{idx + 1}</span>
                <input
                  type="text"
                  value={req}
                  onChange={(e) => handleArrayChange("requirements", idx, e.target.value)}
                  placeholder="Enter requirement or qualification..."
                  className="flex-1 px-4 py-2.5 bg-gray-50 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-red-500 focus:bg-white"
                />
                <button
                  type="button"
                  onClick={() => handleRemoveArrayItem("requirements", idx)}
                  className="p-2 text-gray-400 hover:text-red-600 rounded-lg hover:bg-red-50 transition"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            ))}
          </div>
        </div>

        {/* Benefits & Perks */}
        <div className="bg-white p-6 rounded-2xl border border-gray-200 shadow-sm space-y-4">
          <div className="flex items-center justify-between pb-3 border-b border-gray-100">
            <h2 className="text-lg font-bold text-gray-900">Benefits & Perks</h2>
            <button
              type="button"
              onClick={() => handleAddArrayItem("benefits")}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-gray-100 hover:bg-gray-200 text-gray-700 text-xs font-semibold rounded-lg transition"
            >
              <Plus className="w-3.5 h-3.5" /> Add Benefit
            </button>
          </div>

          <div className="space-y-3">
            {formData.benefits.map((ben, idx) => (
              <div key={idx} className="flex items-center gap-2">
                <span className="w-6 text-xs text-gray-400 font-bold text-center">#{idx + 1}</span>
                <input
                  type="text"
                  value={ben}
                  onChange={(e) => handleArrayChange("benefits", idx, e.target.value)}
                  placeholder="Enter employee benefit or perk..."
                  className="flex-1 px-4 py-2.5 bg-gray-50 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-red-500 focus:bg-white"
                />
                <button
                  type="button"
                  onClick={() => handleRemoveArrayItem("benefits", idx)}
                  className="p-2 text-gray-400 hover:text-red-600 rounded-lg hover:bg-red-50 transition"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            ))}
          </div>
        </div>

        {/* Skills Tags */}
        <div className="bg-white p-6 rounded-2xl border border-gray-200 shadow-sm space-y-4">
          <h2 className="text-lg font-bold text-gray-900 pb-3 border-b border-gray-100">
            Target Skills / Keywords
          </h2>

          <div className="flex gap-2">
            <input
              type="text"
              placeholder="Type skill (e.g. React.js, Sales Strategy, SEO) and click Add"
              value={currentSkill}
              onChange={(e) => setCurrentSkill(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  handleAddSkill(e);
                }
              }}
              className="flex-1 px-4 py-2.5 bg-gray-50 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-red-500 focus:bg-white"
            />
            <button
              type="button"
              onClick={handleAddSkill}
              className="px-5 py-2.5 bg-gray-900 text-white text-sm font-semibold rounded-xl hover:bg-gray-800 transition"
            >
              Add Skill
            </button>
          </div>

          <div className="flex flex-wrap gap-2 pt-2">
            {formData.skills.map((skill) => (
              <span
                key={skill}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-red-50 text-red-700 border border-red-200 rounded-lg text-xs font-semibold"
              >
                {skill}
                <button
                  type="button"
                  onClick={() => handleRemoveSkill(skill)}
                  className="hover:text-red-900 font-bold ml-1"
                >
                  ×
                </button>
              </span>
            ))}
          </div>
        </div>

        {/* Footer Actions */}
        <div className="flex items-center justify-end gap-4 pt-4">
          <Link
            to="/career/jobs"
            className="px-6 py-2.5 border border-gray-200 bg-white hover:bg-gray-50 text-gray-700 rounded-xl text-sm font-semibold transition"
          >
            Cancel
          </Link>
          <button
            type="submit"
            disabled={loading}
            className="inline-flex items-center gap-2 px-8 py-2.5 bg-red-600 hover:bg-red-700 text-white rounded-xl text-sm font-semibold shadow-md transition disabled:opacity-50"
          >
            {loading ? "Publishing..." : "Publish Job Vacancy"}
          </button>
        </div>
      </form>
    </div>
  );
};

export default CareerJobAdd;
